import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  List,
  Stack,
  Table,
  TableCell,
  TableContainer,
  TableRow,
  TextField,
} from "@mui/material";
import React, { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { Button, useNotify, useRecordContext } from "react-admin";
import { blue } from "../../../../Theme/colors";
import { dateConverter } from "../../../../Utils/ConversionUtils";
import {
  deleteLeadNotifications,
  getLeadNotifications,
  saveLeadNotifications,
  updateLeadNotifications,
} from "../../../../Utils/RestUtils";
import "./styles/index.scss";
import { Cancel, Delete, EditNote, Save } from "@mui/icons-material";
import { useGetIdentity } from "ra-core";
import { sendLog } from "../../../../Class/AuditLog";

const AdminNotesPanel = () => {
  const record = useRecordContext();
  const location = useLocation();
  const notify = useNotify();
  const [adminNotes, setAdminNotes] = useState(null);
  const [editNote, setEditNote] = useState(null);
  const [showDialog, setShowDialog] = useState(false);
  const newAdminNoteRef = useRef();
  const updateNoteRef = useRef();
  const { identity, isLoading: identityLoading } = useGetIdentity();

  const saveNewAdminNote = async () => {
    if (newAdminNoteRef.current.value.length <= 0) {
      return;
    }

    const response = await saveLeadNotifications({
      quoteRequestId: record.originId,
      payload: newAdminNoteRef.current.value,
      noteType: 3,
    });

    if (response.result === "success") {
      //reload notes
      getAdminNotesFromApi();
      document.getElementById("new-note").value = "";
    } else {
      notify(response.error_msg, { type: "error" });
    }
  };

  const getAdminNotesFromApi = async () => {
    const result = await getLeadNotifications(record.originId, 3);

    const newDataArray = result.map((row) => {
      const { date, hour } = dateConverter(row.targetTimestamp);

      return {
        ...row,
        date: date,
        hour: hour,
      };
    });

    setAdminNotes(() => newDataArray);
  };

  const handleButtonClick = () => {
    saveNewAdminNote();
  };

  const deleteAdminNotes = async (note) => {
    const response = await deleteLeadNotifications(note.id);
    if (response.result === "success") {
      //reload notes
      sendLog(
        record.originId,
        identity.fullName + " deleted note: " + note.payload
      );
      getAdminNotesFromApi();
    } else {
      notify(response.error_msg, { type: "error" });
    }
  };

  const updateAdminNote = async (note) => {
    const payload = updateNoteRef.current.value;

    const updateResponse = await updateLeadNotifications({
      quoteRequestId: record.originId,
      payload: payload,
      noteType: 3,
      noteId: note.id,
    });

    if (updateResponse.result === "success") {
      //reload notes
      getAdminNotesFromApi();
      sendLog(
        record.originId,
        identity.fullName +
          " changed note from: " +
          note.payload +
          " -> " +
          payload
      );
      document.getElementById("new-note").value = "";
      setShowDialog(false);
    } else {
      notify(updateResponse.error_msg, { type: "error" });
    }
  };

  const handleDeleteNote = (note) => {
    deleteAdminNotes(note);
  };

  const handleCloseClick = () => {
    setShowDialog(false);
    setEditNote(null);
  };

  const handleEditClick = (note) => {
    setEditNote(note);
    setShowDialog(true);
  };

  const handleUpdateNote = (note) => {
    updateAdminNote(note);
  };

  useEffect(() => {
    getAdminNotesFromApi();
  }, [location]);

  const NotesList = () => {
    return (
      <React.Fragment>
        <Dialog
          fullWidth
          open={showDialog}
          onClose={handleCloseClick}
          aria-label="Edit note"
        >
          {editNote && (
            <React.Fragment>
              <DialogTitle>Edit note</DialogTitle>
              <DialogContent>
                <TextField
                  id="updated-note-content"
                  inputRef={updateNoteRef}
                  multiline
                  defaultValue={editNote.payload}
                />
              </DialogContent>
              <DialogActions>
                <Button
                  label="ra.action.save"
                  onClick={(e) => handleUpdateNote(editNote)}
                >
                  <Save />
                </Button>
                <Button label="ra.action.cancel" onClick={handleCloseClick}>
                  <Cancel />
                </Button>
              </DialogActions>
            </React.Fragment>
          )}
        </Dialog>
        {adminNotes &&
          adminNotes.map((note) => {
            return (
              <TableRow key={note.id}>
                <TableCell
                  sx={{
                    fontWeight: "bold",
                    color: blue[900],
                  }}
                >
                  {note.date} {note.timestamp}
                </TableCell>
                <TableCell
                  sx={{
                    fontWeight: "bold",
                  }}
                >
                  {note.admin.displayName}
                </TableCell>
                <TableCell
                  sx={{
                    textTransform: "uppercase",
                    maxWidth: "700px",
                    wordBreak: "break-word",
                  }}
                >
                  {note.payload}
                </TableCell>
                <TableCell
                  sx={{
                    fontWeight: "bold",
                  }}
                >
                  <EditNote
                    sx={{ cursor: "pointer" }}
                    onClick={(e) => handleEditClick(note)}
                  />
                </TableCell>
                <TableCell
                  sx={{
                    fontWeight: "bold",
                  }}
                >
                  <Delete
                    sx={{ cursor: "pointer" }}
                    onClick={(e) => handleDeleteNote(note)}
                  />
                </TableCell>
              </TableRow>
            );
          })}
      </React.Fragment>
    );
  };

  return (
    <React.Fragment>
      <List
        sx={{
          padding: "15px",
          border: "1.5px solid rgba(0, 0, 0, 0.23)",
          borderRadius: "4px",
          boxShadow: "none",
          background: "none",
          marginBottom: "1rem",
        }}
      >
        <TableContainer sx={{ maxHeight: "220px", overflow: "scroll" }}>
          <Table size="small" stickyHeader>
            <NotesList />
          </Table>
        </TableContainer>
      </List>
      <Grid container>
        <Stack
          direction="row"
          justifyContent="center"
          alignItems="center"
          spacing={2}
          sx={{
            width: "100%",
          }}
        >
          <TextField
            name="newAdminNote"
            inputRef={newAdminNoteRef}
            multiline
            label="New Note"
            id="new-note"
            sx={{
              width: "100%",
              "& .MuiInputBase-root": { marginBottom: "0" },
            }}
          />
          <Button color="primary" onClick={handleButtonClick} label="Save" />
        </Stack>
      </Grid>
    </React.Fragment>
  );
};

export default AdminNotesPanel;
